<!-- eslint-disable vue/no-parsing-error -->
<template>
  <v-main class="elegant-background">
    <v-container fluid class="py-6">
      <!-- Device Summary Header -->
      <v-row class="mb-2" dense>
        <v-col cols="12" class="py-1">
          <v-card class="elevation-3">
            <v-card-text class="py-3 px-4 d-flex align-center flex-wrap">
              <div class="d-flex align-center mr-6 mb-2">
                <v-avatar size="40" class="mr-3" color="primary" tile>
                  <v-icon color="white">mdi-water-pump</v-icon>
                </v-avatar>
                <div>
                  <div class="text-subtitle-1 font-weight-medium">Device</div>
                  <div class="text-body-2 grey--text">
                    IMEI: {{ unitdatas.length ? unitdatas[0].imei_number : '—' }}
                  </div>
                </div>
              </div>
              <div class="d-flex align-center mr-6 mb-2">
                <v-chip :color="isDeviceActive ? 'success' : 'error'" dark small>
                  <v-icon left small>{{ isDeviceActive ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
                  {{ isDeviceActive ? 'Active' : 'Inactive' }}
                </v-chip>
              </div>
              <div class="d-flex align-center mr-6 mb-2">
                <v-icon small class="mr-2" color="grey">mdi-clock-outline</v-icon>
                <span class="text-body-2">Last update: {{ lastUpdatedText }}</span>
              </div>
              <v-spacer></v-spacer>
              <div class="d-flex align-center mb-2">
                <v-switch
                  v-model="autoRefresh"
                  inset
                  color="primary"
                  hide-details
                  class="mr-4"
                  label="Auto refresh"
                />
                <v-select
                  :items="refreshIntervalOptions"
                  v-model="refreshInterval"
                  dense
                  hide-details
                  style="max-width: 160px"
                  label="Interval"
                  @change="restartPolling"
                />
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <!-- Key Metrics Dashboard Cards -->
      <v-row class="mb-2" dense>
        <v-col cols="12" md="3" class="py-1">
          <v-card class="metric-card elevation-3 primary-gradient" style="background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%) !important;">
            <v-card-text class="text-white py-3">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-h4 font-weight-bold mb-1">{{ currentLPM || '0' }}</div>
                  <div class="text-body-1">Current LPM</div>
                  <div class="text-caption mt-1">
                    <v-icon small color="white">mdi-water</v-icon>
                    Real-time flow rate
                  </div>
                </div>
                <v-avatar size="56" color="white" class="elevation-2">
                  <v-icon size="32" color="success">mdi-speedometer</v-icon>
                </v-avatar>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <v-col cols="12" md="3" class="py-1">
          <v-card class="metric-card elevation-3 success-gradient" style="background: linear-gradient(135deg, #42a5f5 0%, #1e88e5 100%) !important;">
            <v-card-text class="text-white py-3">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-h4 font-weight-bold mb-1">{{ (totalOutputLitres || 0).toLocaleString() }}</div>
                  <div class="text-body-1">Total Output</div>
                  <div class="text-caption mt-1">
                    <v-icon small color="white">mdi-chart-line</v-icon>
                    Litres produced
                  </div>
                </div>
                <v-avatar size="56" color="white" class="elevation-2">
                  <v-icon size="32" color="info">mdi-water-pump</v-icon>
                </v-avatar>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <v-col cols="12" md="3" class="py-1">
          <v-card class="metric-card elevation-3 info-gradient" style="background: linear-gradient(135deg, #66bb6a 0%, #43a047 100%) !important;">
            <v-card-text class="text-white py-3">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-h4 font-weight-bold mb-1">{{ totalOnTimeHrs || '0' }} hrs</div>
                  <div class="text-body-1">Total On Time</div>
                  <div class="text-caption mt-1">
                    <v-icon small color="white">mdi-clock</v-icon>
                    Hours active
                  </div>
                </div>
                <v-avatar size="56" color="white" class="elevation-2">
                  <v-icon size="32" color="success">mdi-timer</v-icon>
                </v-avatar>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <v-col cols="12" md="3" class="py-1">
          <v-card class="metric-card elevation-3 warning-gradient" style="background: linear-gradient(135deg, #ff7043 0%, #f4511e 100%) !important;">
            <v-card-text class="text-white py-3">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-h4 font-weight-bold mb-1">{{ totalEnergy || '0' }}</div>
                  <div class="text-body-1">Total Energy</div>
                  <div class="text-caption mt-1">
                    <v-icon small color="white">mdi-flash</v-icon>
                    kWh consumed
                  </div>
                </div>
                <v-avatar size="56" color="white" class="elevation-2">
                  <v-icon size="32" color="warning">mdi-lightning-bolt</v-icon>
                </v-avatar>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <!-- Charts and Analytics Section -->
      <v-row class="mb-2 align-stretch" dense>
        <v-col cols="12" lg="8" class="py-1">
          <v-card class="elevation-3">
            <v-card-title class="pb-2">
              <v-icon class="mr-2" color="primary">mdi-chart-areaspline</v-icon>
              Real-time Flow Analytics
              <v-spacer></v-spacer>
              <v-chip small :color="isDeviceActive ? 'success' : 'error'" text-color="white">
                <v-icon small left>{{ isDeviceActive ? 'mdi-circle' : 'mdi-circle-outline' }}</v-icon>
                {{ isDeviceActive ? 'Active' : 'Inactive' }}
              </v-chip>
            </v-card-title>
            <v-card-text>
              <v-progress-linear v-if="isLoading" indeterminate color="primary" class="mb-4"></v-progress-linear>
              <apexchart
                type="area"
                height="400"
                :options="chartOptions"
                :series="series"
              ></apexchart>
            </v-card-text>
          </v-card>
        </v-col>
        
        <v-col cols="12" lg="4" class="py-1">
          <v-card class="elevation-3" style="height: 100%;">
            <v-card-title class="pb-2 d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon class="mr-2" color="primary">mdi-map-marker</v-icon>
                Device Location
              </div>
              <div class="text-caption" v-if="MachineLocation.length > 0" style="line-height: 1.2;">
                <span><strong>Lat:</strong> {{ MachineLocation[0].position.lat.toFixed(4) }}</span> |
                <span><strong>Lng:</strong> {{ MachineLocation[0].position.lng.toFixed(4) }}</span>
              </div>
            </v-card-title>
            <v-card-text class="pa-0 pt-0">
              <GmapMap
                :center="{
                  lat:
                    MachineLocation.length > 0
                      ? parseFloat(MachineLocation[0].position.lat)
                      : 19.601194,
                  lng:
                    MachineLocation.length > 0
                      ? parseFloat(MachineLocation[0].position.lng)
                      : 75.552979,
                }"
                :zoom="12"
                map-type-id="hybrid"
                style="width: 100%; height: 414px; border-radius: 0 0 4px 4px;"
              >
                <GmapMarker
                  v-if="MachineLocation.length > 0"
                  :position="MachineLocation[0].position"
                  :clickable="true"
                  :draggable="false"
                  :icon="{
                    url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
                    scaledSize: { width: 40, height: 40 }
                  }"
                >
                </GmapMarker>
              </GmapMap>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      
      <!-- Filters and Performance Insights -->
      <v-row class="mb-2" dense>
        <v-col cols="12">
          <v-card class="elevation-3">
            <v-card-title class="d-flex align-center justify-space-between flex-wrap">
              <div class="d-flex align-center">
                <v-icon class="mr-2" color="primary">mdi-filter</v-icon>
                <div class="text-h6 font-weight-bold">Filters</div>
              </div>
              <div class="d-flex align-center flex-wrap gap-2">
                <v-menu v-model="menus.from" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="filters.fromDate" label="From Date" dense hide-details readonly v-bind="attrs" v-on="on" prepend-inner-icon="mdi-calendar-start" style="max-width: 180px" />
                  </template>
                  <v-date-picker v-model="filters.fromDate" @change="menus.from = false"></v-date-picker>
                </v-menu>
                <v-menu v-model="menus.to" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="filters.toDate" label="To Date" dense hide-details readonly v-bind="attrs" v-on="on" prepend-inner-icon="mdi-calendar-end" style="max-width: 180px" />
                  </template>
                  <v-date-picker v-model="filters.toDate" @change="menus.to = false"></v-date-picker>
                </v-menu>
                <v-select v-model="filters.status" :items="statusOptions" label="Pump Status" dense hide-details clearable style="max-width: 200px" />
                <v-btn color="primary" small @click="applyFilters"><v-icon left small>mdi-magnify</v-icon>Apply</v-btn>
                <v-btn small text @click="resetFilters"><v-icon left small>mdi-filter-off</v-icon>Reset</v-btn>
              </div>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Performance Insights -->
      <v-row class="mb-4">
        
        <v-col cols="12" md="6">
          <v-card class="elevation-2 minimalistic-card">
            <v-card-text class="py-3">
              <div class="d-flex align-center">
                <v-icon color="info" class="mr-3">mdi-thermometer</v-icon>
                <div>
                  <div class="text-body-2 grey--text">Temperature</div>
                  <div class="text-h6 font-weight-bold info--text">{{ currentTemp }}°C</div>
                </div>
                <v-spacer></v-spacer>
                <v-chip
                  x-small
                  :color="tempStatus.color"
                  text-color="white"
                >
                  {{ tempStatus.text }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <v-col cols="12" md="6">
          <v-card class="elevation-2 minimalistic-card">
            <v-card-text class="py-3">
              <div class="d-flex align-center">
                <v-icon color="warning" class="mr-3">mdi-sine-wave</v-icon>
                <div>
                  <div class="text-body-2 grey--text">Motor Frequency</div>
                  <div class="text-h6 font-weight-bold warning--text">{{ currentFrequency }} Hz</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
        <!-- Data Table Section -->
        <v-col cols="12">
          <v-card class="elevation-3">
            <v-card-title class="d-flex align-center justify-space-between flex-wrap">
              <div class="d-flex align-center">
                <v-icon class="mr-2" color="primary">mdi-database</v-icon>
                <div>
                  <div class="text-h6 font-weight-bold">Device Data Records</div>
                  <div class="d-flex align-center mt-1">
                    <span class="blinking-dot mr-2" v-if="isDeviceActive"></span>
                    <span class="text-body-2" :class="isDeviceActive ? 'success--text' : 'error--text'">
                      {{ isDeviceActive ? 'Live Data from' : 'Inactive -' }}
                      {{ unitdatas.length > 0 ? unitdatas[0].imei_number : 'No Device' }}
                    </span>
                    <v-divider vertical class="mx-3" v-if="unitdatas.length"></v-divider>
                    <v-chip x-small color="indigo" text-color="white" class="mr-2" v-if="unitdatas.length">
                      <v-icon left x-small>mdi-flash</v-icon>
                      {{ latestOutputPower }} kW
                    </v-chip>
                    <v-chip x-small color="teal" text-color="white" class="mr-2" v-if="unitdatas.length">
                      <v-icon left x-small>mdi-water</v-icon>
                      {{ latestFlowRate }} LPM
                    </v-chip>
                    <v-chip x-small color="deep-purple" text-color="white" v-if="unitdatas.length">
                      <v-icon left x-small>mdi-database-export</v-icon>
                      {{ latestTotalOutput }} L
                    </v-chip>
                  </div>
                </div>
              </div>
              
              <div class="d-flex align-center flex-wrap gap-2">
                <v-btn
                  color="success"
                  outlined
                  small
                  @click="getUnitDatas()"
                  :loading="isLoading"
                >
                  <v-icon small left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
                
                <v-btn
                  v-if="unitdatas.length > 0"
                  color="primary"
                  outlined
                  small
                  :loading="isDataExcelloading"
                  @click="generateDataExcel()"
                >
                  <v-icon small left>mdi-microsoft-excel</v-icon>
                  Export Excel
                </v-btn>
              </div>
            </v-card-title>
            
            <v-card-text class="pb-2">
              <div class="d-flex justify-center" v-if="paginationLength > 1">
                <v-pagination
                  v-model="Datapage"
                  :length="paginationLength"
                  :total-visible="7"
                  color="primary"
                  @input="getUnitDatas"
                />
              </div>
            </v-card-text>
            <v-card-text class="pa-0">
              <v-data-table
                :headers="visibleHeaders"
                :items="displayedUnitDatas"
                :loading="isLoading"
                loading-text="Loading data..."
                :server-items-length="serverItemsLength"
                :page.sync="Datapage"
                :items-per-page.sync="rowsPerPage"
                :footer-props="{ 'items-per-page-options': [15, 30, 50, 100] }"
                dense
                fixed-header
                height="520"
                class="improved-table"
              >
                <template v-slot:top>
                  <div class="d-flex align-center justify-space-between px-4 py-2">
                    <div class="text-caption grey--text">Columns</div>
                    <v-menu offset-y>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn v-bind="attrs" v-on="on" small outlined color="primary">
                          <v-icon left small>mdi-view-column</v-icon>
                          Show/Hide
                        </v-btn>
                      </template>
                      <v-list dense>
                        <v-list-item v-for="h in tableHeaders" :key="h.value">
                          <v-list-item-content>
                            <v-checkbox
                              v-model="visibleColumns"
                              :value="h.value"
                              :label="h.text"
                              dense
                              hide-details
                            />
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>
                </template>

                <template v-slot:[`item.pump_status`]="{ item }">
                  <div class="d-flex align-center">
                    <v-chip x-small :color="pumpStatusColor(item.pump_status)" dark>
                      {{ getPumpStatusText(item.pump_status) }}
                    </v-chip>
                    <v-chip x-small outlined color="indigo" class="ml-2" v-if="item.output_power != null">
                      <v-icon left x-small>mdi-flash</v-icon>
                      {{ formatNumber(item.output_power) }} kW
                    </v-chip>
                    <v-chip x-small outlined color="teal" class="ml-2" v-if="item.flow_rate != null">
                      <v-icon left x-small>mdi-water</v-icon>
                      {{ formatNumber(item.flow_rate) }} LPM
                    </v-chip>
                    <v-chip x-small outlined color="deep-purple" class="ml-2" v-if="item.output != null">
                      <v-icon left x-small>mdi-database-export</v-icon>
                      {{ item.output ? parseFloat(item.output).toLocaleString() : '0' }} L
                    </v-chip>
                  </div>
                </template>
                <template v-slot:[`item.voltage`]="{ item }">
                  <div class="text-right">{{ formatNumber(item.voltage) }}<span class="unit"> V</span></div>
                </template>
                <template v-slot:[`item.current`]="{ item }">
                  <div class="text-right">{{ formatNumber(item.current) }}<span class="unit"> A</span></div>
                </template>
                <template v-slot:[`item.power`]="{ item }">
                  <div class="text-right">{{ formatInteger(item.power) }}<span class="unit"> W</span></div>
                </template>
                <template v-slot:[`item.motor_voltage`]="{ item }">
                  <div class="text-right">{{ formatInteger(item.motor_voltage) }}<span class="unit"> V</span></div>
                </template>
                <template v-slot:[`item.motor_current`]="{ item }">
                  <div class="text-right">{{ formatInteger(item.motor_current) }}<span class="unit"> A</span></div>
                </template>
                <template v-slot:[`item.frequency`]="{ item }">
                  <div class="text-right">{{ formatNumber(item.frequency) }}<span class="unit"> Hz</span></div>
                </template>
                <template v-slot:[`item.temperature`]="{ item }">
                  <div class="text-right">{{ formatNumber(item.temperature) }}<span class="unit"> °C</span></div>
                </template>
                <template v-slot:[`item.motor_speed`]="{ item }">
                  <div class="text-right">{{ formatInteger(item.motor_speed) }}<span class="unit"> RPM</span></div>
                </template>
                <template v-slot:[`item.instpower`]="{ item }">
                  <div class="text-right">{{ formatNumber(item.instpower) }}<span class="unit"> kW</span></div>
                </template>
                <template v-slot:[`item.dc`]="{ item }">
                  <div class="text-right">{{ formatNumber(item.dc) }}<span class="unit"> W</span></div>
                </template>
                <template v-slot:[`item.output_power`]="{ item }">
                  <div class="text-right">{{ formatNumber(item.output_power) }}<span class="unit"> kW</span></div>
                </template>
                <template v-slot:[`item.flow_rate`]="{ item }">
                  <div class="text-right">{{ formatNumber(item.flow_rate) }}<span class="unit"> LPM</span></div>
                </template>
                <template v-slot:[`item.output`]="{ item }">
                  <div class="text-right">{{ item.output ? parseFloat(item.output).toLocaleString() : '' }}<span class="unit"> L</span></div>
                </template>

                <template v-slot:no-data>
                  <div class="py-10 text-center">
                    <v-icon x-large color="grey lighten-1">mdi-database-off</v-icon>
                    <div class="mt-2 grey--text">No data found for the selected filters.</div>
                    <v-btn class="mt-3" color="primary" small @click="getUnitDatas">
                      <v-icon left small>mdi-refresh</v-icon>
                      Try Again
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>
<script>
import axios from "axios";
import VueApexCharts from "vue3-apexcharts";
export default {
  name: "DataPage",
  components: {
    apexchart: VueApexCharts,
  },
  data() {
    return {
      unitdatas: [],
      Datapage: 1,
      Datacount: 0,
      DatapaginationLength: 0,
      rowsPerPage: 30,
      DataserialNoStarting: 0,
      MachineLocation: [],
      isDataExcelloading: false,
      isLoading: true,
      autoRefresh: true,
      refreshInterval: 60000,
      refreshIntervalOptions: [
        { text: '15 sec', value: 15000 },
        { text: '30 sec', value: 30000 },
        { text: '1 min', value: 60000 },
        { text: '5 min', value: 300000 },
      ],
      filters: {
        fromDate: "",
        toDate: "",
        status: null,
      },
      menus: {
        from: false,
        to: false,
      },
      statusOptions: [
        { text: 'All', value: null },
        { text: 'SystemOff', value: '00' },
        { text: 'SystemOn', value: '01' },
        { text: 'InputLow', value: '02' },
        { text: 'InputHigh', value: '03' },
        { text: 'OutputLow', value: '04' },
        { text: 'OutputHigh', value: '05' },
        { text: 'BatteryLow', value: '06' },
        { text: 'BatteryHigh', value: '07' },
        { text: 'Overload', value: '08' },
        { text: 'Short', value: '09' },
        { text: 'DSAT', value: '10' },
        { text: 'OverTemperature', value: '11' },
        { text: 'PhaseOpen', value: '12' },
        { text: 'PhaseUnbalance', value: '13' },
        { text: 'GroundFault', value: '14' },
        { text: 'DryRun', value: '15' },
        { text: 'WaterTankFull', value: '16' },
      ],
      tableHeaders: [
        { text: 'Date', value: 'date', align: 'start', width: 110 },
        { text: 'Time', value: 'time', width: 90 },
        { text: 'Pump Status', value: 'pump_status', width: 160 },
        { text: 'Input Voltage (V)', value: 'voltage', align: 'end', width: 140 },
        { text: 'Input Current (A)', value: 'current', align: 'end', width: 150 },
        { text: 'Input Power (W)', value: 'power', align: 'end', width: 150 },
        { text: 'Motor Voltage (V)', value: 'motor_voltage', align: 'end', width: 160 },
        { text: 'Motor Current (A)', value: 'motor_current', align: 'end', width: 160 },
        { text: 'Frequency (Hz)', value: 'frequency', align: 'end', width: 140 },
        { text: 'Temperature (°C)', value: 'temperature', align: 'end', width: 160 },
        { text: 'Motor Speed (RPM)', value: 'motor_speed', align: 'end', width: 170 },
        { text: 'Inst Power (kW)', value: 'instpower', align: 'end', width: 150 },
        { text: 'DC Power (W)', value: 'dc', align: 'end', width: 140 },
        { text: 'Output Power (kW)', value: 'output_power', align: 'end', width: 170 },
        { text: 'Flow Rate (LPM)', value: 'flow_rate', align: 'end', width: 150 },
        { text: 'Total Output (L)', value: 'output', align: 'end', width: 170 },
      ],
      visibleColumns: ['date','time','pump_status','voltage','current','power','motor_voltage','motor_current','frequency','temperature','motor_speed','instpower','dc','output_power','flow_rate','output'],
      TotalFlow: 0,
      LastTotalFlow: 0,
      CurrentFlow: 0,
      LastCurrentFlow: 0,
      LatestOutput: 0,
      LastLatestOutput: 0,
      Frequency: 0,
      LastFrequency: 0,
      // Enhanced chart options
      series: [
        {
          name: "Flow Rate (LPM)",
          data: [],
          color: "#667eea",
        },
        {
          name: "Power (W)",
          data: [],
          color: "#f093fb",
        },
      ],
      chartOptions: {
        chart: {
          height: 380,
          type: "area",
          background: 'transparent',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          zoom: {
            enabled: true,
            type: 'x',
            autoScaleYaxis: true,
          },
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
            }
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 1200,
            animateGradually: {
              enabled: true,
              delay: 150
            },
          },
          dropShadow: {
            enabled: true,
            color: '#000',
            top: 18,
            left: 7,
            blur: 10,
            opacity: 0.1
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: "smooth",
          width: [4, 3],
          lineCap: 'round',
        },
        fill: {
          type: "gradient",
          gradient: {
            shade: 'dark',
            gradientToColors: ['#764ba2', '#f093fb'],
            shadeIntensity: 1,
            type: "vertical",
            opacityFrom: 0.8,
            opacityTo: 0.1,
            stops: [0, 100, 100, 100]
          }
        },
        grid: {
          show: true,
          borderColor: 'rgba(255, 255, 255, 0.1)',
          strokeDashArray: 0,
          xaxis: {
            lines: {
              show: false
            }
          },   
          yaxis: {
            lines: {
              show: true
            }
          },
        },
        xaxis: {
          categories: this.generateTimeCategories(),
          title: {
            text: 'Time (24H Format)',
            style: {
              fontSize: '14px',
              fontWeight: 600,
              color: '#64748b'
            }
          },
          labels: {
            style: {
              fontSize: '12px',
              colors: '#64748b',
              fontWeight: 500,
            }
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          }
        },
        yaxis: [
          {
            title: {
              text: 'Flow Rate (LPM)',
              style: {
                color: '#667eea',
                fontSize: '14px',
                fontWeight: 600,
              }
            },
            labels: {
              style: {
                colors: '#667eea',
                fontSize: '12px',
                fontWeight: 500,
              }
            },
            axisBorder: {
              show: false,
            },
          },
          {
            opposite: true,
            title: {
              text: 'Power (W)',
              style: {
                color: '#f093fb',
                fontSize: '14px',
                fontWeight: 600,
              }
            },
            labels: {
              style: {
                colors: '#f093fb',
                fontSize: '12px',
                fontWeight: 500,
              }
            },
            axisBorder: {
              show: false,
            },
          }
        ],
        legend: {
          show: true,
          position: 'top',
          horizontalAlign: 'right',
          floating: true,
          offsetY: -25,
          offsetX: -5,
          fontSize: '14px',
          fontWeight: 600,
          labels: {
            colors: '#64748b',
          },
          markers: {
            width: 12,
            height: 12,
            radius: 6,
          }
        },
        tooltip: {
          enabled: true,
          shared: true,
          followCursor: true,
          intersect: false,
          theme: 'dark',
          style: {
            fontSize: '14px',
            fontFamily: 'Inter, sans-serif',
          },
          y: {
            formatter: function (val, { seriesIndex }) {
              if (seriesIndex === 0) {
                return val.toFixed(2) + " LPM";
              } else {
                return val.toFixed(0) + " W";
              }
            }
          }
        },
        responsive: [{
          breakpoint: 768,
          options: {
            chart: {
              height: 300,
            },
            legend: {
              position: 'bottom',
              horizontalAlign: 'center',
              floating: false,
              offsetY: 0,
              offsetX: 0,
            }
          }
        }]
      },
      pollingInterval: null,
    };
  },
  computed: {
    isDeviceActive() {
      return this.unitdatas.length > 0 && this.unitdatas[0].unit && this.unitdatas[0].unit.is_active;
    },
    tempStatus() {
      if (this.currentTemp < 30) return { color: 'success', text: 'Normal' };
      if (this.currentTemp < 50) return { color: 'warning', text: 'Warm' };
      return { color: 'error', text: 'Hot' };
    },
    visibleHeaders() {
      return this.tableHeaders.filter(h => this.visibleColumns.includes(h.value));
    },
    lastUpdatedText() {
      if (!this.unitdatas.length) return '—';
      const d = this.unitdatas[0];
      return `${d.date || ''} ${d.time || ''}`.trim() || '—';
    },
    latestOutputPower() {
      if (!this.unitdatas.length) return '0';
      const v = parseFloat(this.unitdatas[0].output_power) || 0;
      return this.formatNumber(v);
    },
    latestFlowRate() {
      if (!this.unitdatas.length) return '0';
      const v = parseFloat(this.unitdatas[0].flow_rate) || 0;
      return this.formatNumber(v);
    },
    latestTotalOutput() {
      if (!this.unitdatas.length) return '0';
      const v = parseFloat(this.unitdatas[0].output) || 0;
      return v.toLocaleString();
    },
    filteredUnitDatas() {
      let items = this.unitdatas.slice();
      const from = this.parseDate(this.filters.fromDate);
      const to = this.parseDate(this.filters.toDate);
      items = items.filter(i => {
        const rowDate = this.parseDate(i.date);
        if (!rowDate) return false;
        if (from && rowDate.getTime() < from.getTime()) return false;
        if (to) {
          // Include the entire 'to' day by advancing to end-of-day
          const toEnd = new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999);
          if (rowDate.getTime() > toEnd.getTime()) return false;
        }
        return true;
      });
      if (this.filters.status) {
        items = items.filter(i => String(i.pump_status).padStart(2, '0') === this.filters.status);
      }
      return items;
    },
    serverItemsLength() {
      return this.filteredUnitDatas.length || this.Datacount;
    },
    paginationLength() {
      return Math.ceil(this.serverItemsLength / this.rowsPerPage) || 1;
    },
    displayedUnitDatas() {
      const start = (this.Datapage - 1) * this.rowsPerPage;
      const end = start + this.rowsPerPage;
      return this.filteredUnitDatas.slice(start, end);
    },
  },
  mounted() {
    if (this.roleName == "Super Admin") {
      this.companyId = this.$store.getters["settings/getCompanyId"];
    }
    this.getUnitDatas();
    this.startPolling();
  },
  beforeDestroy() {
    clearInterval(this.pollingInterval); // Stop polling when the component is destroyed
  },
  methods: {
    parseDate(val) {
      if (!val) return null;
      // Accept both 'YYYY-MM-DD' and 'DD-MM-YYYY'
      if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
        const [y, m, d] = val.split('-').map(n => parseInt(n, 10));
        return new Date(y, m - 1, d);
      }
      if (/^\d{2}-\d{2}-\d{4}$/.test(val)) {
        const [d, m, y] = val.split('-').map(n => parseInt(n, 10));
        return new Date(y, m - 1, d);
      }
      // Fallback: try Date.parse
      const dt = new Date(val);
      return isNaN(dt.getTime()) ? null : dt;
    },
    async getUnitDatas() {
      this.isLoading = true;
      try {
        let query =
          "page=" + this.Datapage + "&rowsPerPage=" + this.rowsPerPage + "&status=true";
        if (this.filters.fromDate) {
          query += `&from_date=${encodeURIComponent(this.filters.fromDate)}`;
        }
        if (this.filters.toDate) {
          query += `&to_date=${encodeURIComponent(this.filters.toDate)}`;
        }
        if (this.filters.status) {
          query += `&pump_status=${encodeURIComponent(this.filters.status)}`;
        }
        let response = "";
        if (this.roleName == "Super Admin") {
          const config = {
            headers: {
              "Company-id": this.companyId,
            },
          };
          response = await axios.get(
            `units/${this.$route.params.unitId}/datas?${query}`,
            config
          );
        } else {
          response = await axios.get(
            `units/${this.$route.params.unitId}/datas?${query}`
          );
        }
        this.unitdatas = response.data.data;
        if(this.unitdatas != null){
          this.updateChart();
        }
        // Update key metrics
        this.currentLPM = this.unitdatas.length > 0 ? (this.unitdatas[0].flow_rate || 0) : 0;
        this.totalOutputLitres = this.calculateTotalOutput();
        this.totalOnTimeHrs = this.calculateTotalOnTime();
        this.totalEnergy = this.calculateTotalEnergy();
        this.currentTemp = this.unitdatas.length > 0 ? (this.unitdatas[0].temperature || 0) : 0;
        this.currentFrequency = this.unitdatas.length > 0 ? (this.unitdatas[0].frequency || 0) : 0;
        
        // Legacy compatibility
        this.TotalFlow = response.data.total_flow;
        this.CurrentFlow = response.data.current_flow;
        this.LatestOutput = response.data.latest_output;
        this.Frequency = response.data.frequency;
        this.title = this.unitdatas[0].imei_number;
        this.unitdatas.forEach((unit) => {
          this.MachineLocation.push({
            position: {
              lat: parseFloat(unit.latitude),
              lng: parseFloat(unit.longitude),
            },
          });
        });
        this.Datacount = response.data.count;
        this.DataserialNoStarting = (this.Datapage - this.rowsPerPage) * 1;
        this.DatapaginationLength = Math.ceil(this.Datacount / this.rowsPerPage);
      } catch (error) {
        console.log("error", error);
        this.isLoading = false;
      }
      this.isLoading = false;
    },
    startPolling() {
      this.stopPolling();
      if (!this.autoRefresh) return;
      this.pollingInterval = setInterval(async () => {
        await this.getUnitDatas();
      }, this.refreshInterval);
    },
    stopPolling() {
      if (this.pollingInterval) clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    },
    restartPolling() {
      this.startPolling();
    },
    async generateDataExcel() {
      this.isDataExcelloading = true;
      let response = [];
      try {
        if (this.roleName == "Super Admin") {
          const config = {
            headers: {
              "Company-id": this.companyId,
            },
          };
          response = await axios.get(
            `units/${this.unitdatas[0].unit.id}/unitDatasExport`,
            config
          );
        } else {
          response = await axios.get(
            `units/${this.unitdatas[0].unit.id}/unitDatasExport`
          );
        }
        if (response && response.data && response.data.data) {
          window.open(`${response.data.data}`, "_blank");
        } else {
          console.error("Invalid response data:", response.data);
        }
      } catch (error) {
        console.error("Error fetching Excel data:", error);
      }
      this.isDataExcelloading = false;
    },
    generateTimeCategories() {
      const currentHour = new Date().getHours();
      const categories = [];
      for (let hour = 0; hour <= currentHour; hour++) {
        const time = `${hour < 10 ? "0" + hour : hour}:00`;
        categories.push(time);
      }
      return categories;
    },
    updateChart() {
      const currentHour = new Date().getHours();
      const hourlyFlows = Array.from({ length: currentHour + 1 }, () => 0);
      const hourlyPowers = Array.from({ length: currentHour + 1 }, () => 0);
      
      // Organize data by hour for more accurate representation
      const hourlyData = {};
      
      this.unitdatas.forEach((unit) => {
        if (unit.time) {
          const hour = parseInt(unit.time.split(":")[0], 10);
          if (hour <= currentHour) {
            if (!hourlyData[hour]) {
              hourlyData[hour] = { flows: [], powers: [] };
            }
            hourlyData[hour].flows.push(parseFloat(unit.flow_rate) || 0);
            hourlyData[hour].powers.push(parseFloat(unit.power) || 0);
          }
        }
      });
      
      // Calculate averages for each hour
      for (let i = 0; i <= currentHour; i++) {
        if (hourlyData[i]) {
          const avgFlow = hourlyData[i].flows.reduce((a, b) => a + b, 0) / hourlyData[i].flows.length;
          const avgPower = hourlyData[i].powers.reduce((a, b) => a + b, 0) / hourlyData[i].powers.length;
          hourlyFlows[i] = Number(avgFlow.toFixed(2));
          hourlyPowers[i] = Number(avgPower.toFixed(2));
        }
      }
      
      // Update both series
      this.series = [
        {
          name: "Flow Rate (LPM)",
          data: hourlyFlows,
          color: "#1976D2",
        },
        {
          name: "Power (W)", 
          data: hourlyPowers,
          color: "#388E3C",
        }
      ];
      
      // Update x-axis categories
      this.chartOptions = {
        ...this.chartOptions,
        xaxis: {
          ...this.chartOptions.xaxis,
          categories: this.generateTimeCategories()
        }
      };
    },
    
    calculateTotalOutput() {
      if (!this.unitdatas.length) return 0;
      // Get the output from the latest/first data entry as it represents total output
      const latestOutput = parseFloat(this.unitdatas[0].output) || 0;
      return Math.round(latestOutput);
    },
    
    calculateTotalOnTime() {
      if (!this.unitdatas.length) return 0;
      // Get the total_on_time from the latest/first data entry (in minutes) and convert to hours
      const latestOnTimeMinutes = parseFloat(this.unitdatas[0].total_on_time) || 0;
      const latestOnTimeHours = latestOnTimeMinutes / 60;
      return latestOnTimeHours.toFixed(1);
    },
    
    calculateTotalEnergy() {
      if (!this.unitdatas.length) return 0;
      const totalWatts = this.unitdatas.reduce((sum, data) => {
        return sum + (parseFloat(data.power) || 0);
      }, 0);
      return (totalWatts / 1000).toFixed(2); // Convert to kWh
    },
    
    getPumpStatusText(statusCode) {
      const statusMap = {
        '00': 'SystemOff',
        '01': 'SystemOn',
        '02': 'InputLow',
        '03': 'InputHigh',
        '04': 'OutputLow',
        '05': 'OutputHigh',
        '06': 'BatteryLow',
        '07': 'BatteryHigh',
        '08': 'Overload',
        '09': 'Short',
        '10': 'DSAT',
        '11': 'OverTemperature',
        '12': 'PhaseOpen',
        '13': 'PhaseUnbalance',
        '14': 'GroundFault',
        '15': 'DryRun',
        '16': 'WaterTankFull'
      };
      
      // Handle both string and number inputs
      const code = String(statusCode).padStart(2, '0');
      return statusMap[code] || statusCode || '';
    },
    pumpStatusColor(code) {
      const text = this.getPumpStatusText(code);
      const map = {
        'SystemOn': 'success',
        'SystemOff': 'grey',
        'OverTemperature': 'error',
        'Overload': 'error',
        'Short': 'error',
        'InputLow': 'warning',
        'InputHigh': 'warning',
        'OutputLow': 'info',
        'OutputHigh': 'info',
        'DryRun': 'orange',
        'BatteryLow': 'warning',
        'BatteryHigh': 'warning',
      };
      return map[text] || 'primary';
    },
    formatNumber(val) {
      const n = parseFloat(val);
      if (isNaN(n)) return '';
      return n.toFixed(2);
    },
    formatInteger(val) {
      const n = parseFloat(val);
      if (isNaN(n)) return '';
      return Math.round(n).toLocaleString();
    },
    applyFilters() {
      this.Datapage = 1;
      // client-side filtering; no server call needed
    },
    resetFilters() {
      this.filters.fromDate = "";
      this.filters.toDate = "";
      this.filters.status = null;
      this.Datapage = 1;
      // client-side filtering; no server call needed
    },
    exportCsv() {
      if (!this.unitdatas || !this.unitdatas.length) return;
      const headers = this.visibleHeaders.map(h => h.text);
      const keys = this.visibleHeaders.map(h => h.value);
      const rows = this.unitdatas.map(r => keys.map(k => r[k] != null ? r[k] : ''));
      const csv = [headers.join(','), ...rows.map(a => a.join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'unit-data.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  },
};
</script>
<style scoped>
/* Professional dashboard background */
.elegant-background {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
  position: relative;
}

.elegant-background::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    linear-gradient(90deg, transparent 50%, rgba(255,255,255,0.02) 50%),
    linear-gradient(0deg, transparent 50%, rgba(255,255,255,0.02) 50%);
  background-size: 50px 50px;
  pointer-events: none;
}

/* Enhanced styling for the new layout */
.metric-card {
  transition: all 0.3s ease;
  border-radius: 12px !important;
}

.metric-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(0,0,0,0.15) !important;
}

.primary-gradient {
  background: linear-gradient(135deg, #1976D2 0%, #1565C0 100%);
}

.success-gradient {
  background: linear-gradient(135deg, #388E3C 0%, #2E7D32 100%);
}

.info-gradient {
  background: linear-gradient(135deg, #0288D1 0%, #0277BD 100%);
}

.warning-gradient {
  background: linear-gradient(135deg, #F57C00 0%, #EF6C00 100%);
}

.performance-card {
  height: 100%;
  transition: all 0.3s ease;
}

.performance-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0,0,0,0.1) !important;
}

.minimalistic-card {
  transition: all 0.2s ease;
  border-radius: 8px !important;
  background: #ffffff !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.minimalistic-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
}

.gap-2 {
  gap: 8px;
}

/* Legacy data card styling for backward compatibility */
.data-tab .data-card {
  border-right: 1px solid #5a5a5a47;
}
.blinking-dot {
  width: 10px;
  height: 10px;
  background-color: red;
  border-radius: 50%;
  display: inline-block;
  animation: blink 1s infinite;
}

@keyframes blink {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

/* Enhanced table styling */
.v-data-table {
  border-radius: 8px;
}

.v-card-title {
  background: linear-gradient(90deg, rgba(248,249,250,0.8) 0%, rgba(255,255,255,0.9) 100%);
  border-bottom: 1px solid rgba(224,224,224,0.3);
  backdrop-filter: blur(5px);
}

/* Glass morphism for all cards */
.v-card {
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.v-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 40px rgba(0,0,0,0.15) !important;
  background: rgba(255, 255, 255, 0.95) !important;
  transition: all 0.3s ease !important;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .metric-card {
    margin-bottom: 16px;
  }
}

/* Chart container enhancements */
.apexcharts-canvas {
  border-radius: 8px;
}

/* Map styling */
.gm-style {
  border-radius: 0 0 8px 8px;
}
</style>
